import { Box, Button, Flex, Image, Input, useToast } from '@chakra-ui/react'
import {
  useDeleteLogo,
  useUploadLogo,
} from 'brokertslib/src/hooks/useOrganizationUnit'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'

import { baseUrl } from '@/hooks'
import { useAuth } from '../../context/AuthContext'
import { Trash, Upload } from '../../icons'

const ImageWrapper = ({ url }: { url: string | null }) => {
  return (
    <Box
      sx={{
        w: url ? 'auto' : '8rem',
        h: '5rem',
        display: 'block',
        border: url ? 'none' : '1px dashed',
        borderColor: 'gray.500',
        borderRadius: 'md',
        color: 'gray.500',
        alignContent: 'center',
        textAlign: 'center',
      }}
    >
      {url ? (
        <Image src={url} alt="logo" objectFit="contain" w="full" h="full" />
      ) : (
        'Žádné logo'
      )}
    </Box>
  )
}

export const OrganizationLogoUploadForm = ({
  organizationUnitId,
  logoUrl,
}: {
  organizationUnitId: number
  logoUrl?: string
}) => {
  const auth = useAuth()
  const toast = useToast()
  const [url, setUrl] = useState<string | null>(logoUrl ?? null)
  const methods = useForm<{ file: FileList }>()
  const { register, handleSubmit, getValues, reset } = methods

  const { mutateAsync: uploadLogoAsync, isPending: isUploadLoading } =
    useUploadLogo()
  const { mutateAsync: deleteLogoAsync, isPending: isDeleteLoading } =
    useDeleteLogo()

  const upload = async (values: { file: FileList }) => {
    const formData = new FormData()
    formData.append('file', values.file.item(0) as File)

    await uploadLogoAsync({ formData, ouId: organizationUnitId })
      .then((result) => {
        setUrl(result.data + '?' + new Date().getTime())
        toast({
          title: 'Logo bylo nahráno',
          status: 'success',
        })
      })
      .catch((e) =>
        toast({
          title: 'Nahrání loga se nezdařilo',
          status: 'error',
        }),
      )

    reset()
  }

  const deleteLogo = async () => {
    await deleteLogoAsync(organizationUnitId)
      .then(() => setUrl(null))
      .catch((e) =>
        toast({
          title: 'Smazání loga se nezdařilo',
          status: 'error',
        }),
      )
  }

  useEffect(() => {
    setUrl(logoUrl ?? null)
  }, [logoUrl])

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(upload)}>
        <Flex>
          <Flex
            sx={{
              gap: 4,
              mt: 4,
              p: 3,
              backgroundColor: 'gray.50',
              borderRadius: 'md',
            }}
          >
            {url && <ImageWrapper url={baseUrl + '/' + url} />}

            {auth?.user?.allowEditOrganizationStructure && (
              <Box sx={{ display: 'grid', gap: 3 }}>
                <Input
                  id="fileInput"
                  display={'none'}
                  type="file"
                  {...register('file', { onChange: () => upload(getValues()) })}
                  multiple={false}
                  accept=".jpg, .png, .jpeg, .gif, .bmp, .tif, .svg, .tiff|image/*"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('fileInput')?.click()}
                  isLoading={isUploadLoading}
                >
                  <Upload color="primary.500" />
                  Nahrát logo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  colorScheme="red"
                  onClick={deleteLogo}
                  isLoading={isDeleteLoading}
                >
                  <Trash color="red.700" />
                  Smazat logo
                </Button>
              </Box>
            )}
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  )
}
